import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  onAuthStateChanged,
  RecaptchaVerifier,
} from "firebase/auth";
import { Dialog } from "@material-ui/core";
import logogif from "../images/logo-progress.gif";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  const [loginMethod, setLoginMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  // Get redirect path from query param
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get("redirect") || "pricing";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        if (user.phoneNumber) {
          navigate(`/${redirectPath}?uid=${user.uid}`);
        } else {
          navigate(`/verify-phonenumber/${user.uid}`);
        }
      }
    });
    return () => unsubscribe();
  }, [auth, navigate, redirectPath]);

  const handleLoginMethodChange = (method) => {
    setLoginMethod(method);
    setShowOtpInput(false);
  };

  const handleEmailPasswordLogin = async () => {
    try {
      setLoading(true);
      setOpenDialog(true);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      if (user.phoneNumber) {
        navigate(`/${redirectPath}?uid=${user.uid}`);
      } else {
        navigate(`/verify-phonenumber/${user.uid}`);
      }
    } catch (error) {
      console.error("Email login error:", error);
      alert("Login failed: " + error.message);
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  const onCaptchaVerify = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "normal",
          callback: (response) => {
            console.log("reCAPTCHA verified:", response);
          },
          "expired-callback": () => {},
        },
        auth
      );
    }
  };

  const onSendOtp = async () => {
    try {
      onCaptchaVerify();
      setLoading(true);
      setOpenDialog(true);
      const appVerifier = window.recaptchaVerifier;
      const formatPh = "+91" + phoneNumber;
      const result = await signInWithPhoneNumber(auth, formatPh, appVerifier);
      setConfirmationResult(result);
      setShowOtpInput(true);
      alert("OTP sent successfully!");
    } catch (error) {
      console.error("OTP error:", error);
      alert("Error sending OTP: " + error.message);
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  const onOtpVerify = async () => {
    try {
      if (!confirmationResult) return;
      setLoading(true);
      setOpenDialog(true);
      const res = await confirmationResult.confirm(otp);
      const user = res.user;
      if (user.phoneNumber) {
        navigate(`/${redirectPath}?uid=${user.uid}`);
      } else {
        navigate(`/verify-phonenumber/${user.uid}`);
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      alert("Invalid OTP");
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  const handleLogin = () => {
    if (loginMethod === "email") {
      handleEmailPasswordLogin();
    } else {
      onOtpVerify();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-150  p-4 mt-6">
      <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-2xl">
        <h2 className="text-3xl font-bold text-center text-[#2d3191] mb-6">
          Login
        </h2>

        <div id="recaptcha-container" className="mb-4"></div>

        <div className="flex justify-center mb-6">
          <button
            className={`px-4 py-2 rounded-l-full border ${
              loginMethod === "email"
                ? "bg-[#2d3191] text-white"
                : "bg-gray-100 text-gray-700"
            }`}
            onClick={() => handleLoginMethodChange("email")}
          >
            Email/Password
          </button>
          <button
            className={`px-4 py-2 rounded-r-full border ${
              loginMethod === "phone"
                ? "bg-[#2d3191] text-white"
                : "bg-gray-100 text-gray-700"
            }`}
            onClick={() => handleLoginMethodChange("phone")}
          >
            Phone Number
          </button>
        </div>

        {loginMethod === "email" && (
          <div className="flex flex-col gap-4">
            <input
              type="email"
              placeholder="Email"
              className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}

        {loginMethod === "phone" && (
          <div className="flex flex-col gap-4">
            <input
              type="tel"
              placeholder="Phone Number"
              className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <button
              className="w-full bg-green-700 text-white py-2 rounded-lg hover:bg-green-800 transition"
              onClick={onSendOtp}
            >
              Send OTP
            </button>
          </div>
        )}

        {showOtpInput && (
          <div className="flex flex-col gap-4 mt-4">
            <input
              type="text"
              placeholder="Enter OTP"
              className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3191]"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
          </div>
        )}

        <button
          className="w-full bg-[#2d3191] text-white py-3 mt-6 rounded-lg font-semibold hover:bg-indigo-900 transition"
          onClick={handleLogin}
        >
          Login
        </button>

        <div className="text-center mt-6">
          <p className="text-gray-600">Don't have an account?</p>
          <a
            href="/signup"
            className="text-green-700 font-semibold hover:underline"
          >
            Create one
          </a>
        </div>

        <Dialog open={openDialog}>
          <div className="p-8 flex flex-col items-center">
            <img src={logogif} alt="Loading..." className="w-16 h-16 mb-4" />
            <p className="text-[#2d3191] font-semibold">
              {loading ? "Processing..." : "Done"}
            </p>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default Login;
